import React, { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { TopNav } from 'shared/components/TopNav/index.js';
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline'
import { getUrlToLoginCallbackPage, getUrlToLoginPage, getUrlToPrivacyPolicyPage, getUrlToTermsAndConditionsPage } from "pages/urls";
import { ActionButton } from "components/ActionButton";
import { TextInput } from "components/TextInput";
import { PasswordInput } from "components/PasswordInput";
import { SignupLoginAgreement } from "containers/SignupLoginAgreement";
import antidoteLogoSrc from "../../images/antidote-logo.png";
import { signup } from "api";
import { HttpApiResponseError } from "shared/http-api";
import { isLoggedIn } from "api/auth-storage";
import { executeRecaptcha } from "api/recaptcha";

const SUCCESS_REDIRECT_TIMEOUT = 3000;

const LOGIN_CALLBACK_PAGE_URL = getUrlToLoginCallbackPage();
const LOGIN_PAGE_URL = getUrlToLoginPage();

export const SignupPage = () => {
  const navigate = useNavigate();
  const isAuthenticated = isLoggedIn();
  const [urlSearchParams] = useSearchParams();

  const loginCallbackPageUrl = LOGIN_CALLBACK_PAGE_URL + '?' + urlSearchParams.toString();
  const loginPageUrl = LOGIN_PAGE_URL + '?' + urlSearchParams.toString();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    password: "",
    password2: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string,string>>({});

  const onChange = (e) => {
    setErrors({});
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onConfirmChange = (e) => {
    onChange(e);
    const pass = e.target.value;
    const length = pass.length;
    const check = formData.password.substring(0, length);
    if(pass !== check) {
      setErrors({ password2: "Passwords do not match" });
    }
  };

  const checkErrors = () => {
    const errors: Record<string,string> = {};
    if (!formData.name) errors.name = 'Full name is required';
    if (!formData.phoneNumber) errors.phoneNumber = 'Phone number is required';
    else if (parseInt(formData.phoneNumber).toString().length !== 10) errors.phoneNumber = 'Phone number must be 10 digits';
    if (!formData.email) errors.email = 'Email is required';
    else if (!formData.email.match(/^\S+@\S+\.\S+$/)) errors.email = 'Enter a valid email';
    if (!formData.password) errors.password = 'Password is required';
    else if (formData.password.length < 6) errors.password = 'Password must be atleast 6 characters';
    if (!formData.password2) errors.password2 = 'Password confirmation is required';
    if (formData.password !== formData.password2) errors.password2 = 'Passwords do not match';
    setErrors(errors);
    return Object.keys(errors).length > 0;
  }

  const canSubmit = !submitting && Object.keys(errors).length === 0

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (checkErrors()) return;
    setSubmitting(true);
    const { name, phoneNumber, email, password } = formData;
    try {
      const recaptchaToken = await executeRecaptcha('signup');
      await signup({ name, email, phoneNumber, password, token: recaptchaToken });
      setSignupSuccess(true);
      setTimeout(() => navigate(loginCallbackPageUrl), SUCCESS_REDIRECT_TIMEOUT);
    } catch (err) {
      if (!(err instanceof HttpApiResponseError)) {
        setErrors({ name: 'Could not create your account because of an unknown error' });
      } else {
        const response = await err.json();
        if (!response.errors || response.errors.length === 0) {
          setErrors({ name: 'Could not create your account because of an unknown error' });
        } else {
          setErrors(Object.fromEntries(response.errors.map(err => [err.path, err.message])));
        }
      }
    }
    setSubmitting(false);
  };

  if (signupSuccess) {
    return (
      <div className="pt-[200] text-center text-xl">
        <div className="text-2xl mb-5">Welcome to Antidote!</div>
        <CheckCircleOutline color="action" style={{ width: '4.8rem', height: '4.8rem' }} />
        <div className="text-lg mt-5">Your account has been created.</div>
        <div> You will be redirected shortly. </div>
      </div>
    )
  }

  // Redirect if logged in
  if (isAuthenticated) {
    return <Navigate to={loginCallbackPageUrl} />;
  }

  return (
    <div className="px-6 py-4">
      <TopNav onBackClick={() => navigate(loginPageUrl)} />
      <div className="max-w-[350] mx-auto flex flex-col justify-center mt-12 gap-2">
      <img src={antidoteLogoSrc} alt='Antidote Logo' className="w-full" />
        <div className="text-lg font-medium text-gray-900 mb-6 text-center">Create your Antidote account</div>
        <form className="" noValidate onSubmit={(e) => onSubmit(e)}>
          <TextInput
            label="Full Name"
            name="name"
            type="name"
            value={formData.name}
            onChange={(val, e) => onChange(e)}
            error={errors.name}
            placeholder="Enter your name"
            required
            disabled={submitting}
          />
          <TextInput
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={(val, e) => onChange(e)}
            error={errors.email}
            placeholder="Enter your email"
            required
            disabled={submitting}
          />
          <TextInput
            label="Phone Number"
            name="phoneNumber"
            type="phoneNumber"
            value={formData.phoneNumber}
            onChange={(val, e) => onChange(e)}
            error={errors.phoneNumber}
            placeholder="Enter your phone number"
            required
            disabled={submitting}
          />
          <PasswordInput
            label="Password"
            name="password"
            value={formData.password}
            onChange={(val, e) => onChange(e)}
            placeholder="Enter your password"
            required
            error={errors.password}
            disabled={submitting}
          />
          <PasswordInput
            label="Confirm Password"
            name="password2"
            value={formData.password2}
            onChange={(val, e) => onConfirmChange(e)}
            placeholder="Confirm your password"
            required
            error={errors.password2}
            disabled={submitting}
          />
          <SignupLoginAgreement />
          <div className="mt-8">
            <ActionButton
              type="submit"
              label='Create Account'
              workingLabel='Creating Your Account'
              disabled={!canSubmit}
              working={submitting}
            />
          </div>
          <div className="text-red-500 text-sm mb-2 min-h-[20px]">{errors.token}</div>
        </form>
        <div className="text-gray-500 mt-2">
          <span>Already signed up? </span>
          <Link
            to={loginPageUrl}
            className="font-bold text-violet-600"
          >
            Login
          </Link>
        </div>
      </div>
    </div>
  );
};
